import { useCallback, useRef, useState } from "react"
import { useDropzone } from "react-dropzone"
import { useQueryClient } from "@tanstack/react-query"
import { isAxiosError } from "axios"
import { ImageDropzone } from "components/ui/ImageDropzone/ImageDropzone"
import { Modal } from "components/ui/modals/Modal/Modal"
import { ButtonSecondary } from "components/ui/buttons/ButtonSecondary/ButtonSecondary"
import { ButtonWarning } from "components/ui/buttons/ButtonWarning/ButtonWarning"
import { useMessage } from "hooks/message.hook"
import { useUploadModelNumberImage } from "hooks/api/modelNumbers/modelNumbers.hook"
import { useApiErrorFormatter } from "hooks/apiErrorFormatter.hook"
import { convertDataUrlToFile } from "helpers/utils.helper"
import { EditImageModal } from "components/ui/modals/EditImageModal/EditImageModal"
import style from "./Image.module.scss"

const MAX_IMAGE_SIZE_MB = Number(import.meta.env.VITE_MAX_IMAGE_SIZE_MB) || 15
const MAX_IMAGE_SIZE_BYTES = MAX_IMAGE_SIZE_MB * 1024 * 1024

const { imageBlock, image, replaceButton, deleteButton, deleteModalButtons } = style

type Props = {
	modelNumberId?: number
	modelNumberImageLink: string | undefined
	modelNumberImageError: unknown
}

export const Image = ({ modelNumberId, modelNumberImageLink, modelNumberImageError }: Props) => {
	const queryClient = useQueryClient()
	const { error, success } = useMessage()
	const { formatError } = useApiErrorFormatter()
	const { mutate: uploadModelNumberImage, isPending: isUploadPending } = useUploadModelNumberImage()
	const [isModalOpen, setIsModalOpen] = useState(false)
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
	const [uploadedImage, setUploadedImage] = useState<File | null>(null)
	const [croppedImageUrl, setCroppedImageUrl] = useState<string | null>(null)
	const [isImageDeleted, setIsImageDeleted] = useState(false)
	const replaceInputRef = useRef<HTMLInputElement>(null)

	const isMissingImageError =
		isAxiosError(modelNumberImageError) &&
		modelNumberImageError.response?.status === 404 &&
		modelNumberImageError.response?.data?.error === "Stevens model number image is missing"
	const imageLinkFromApi = isMissingImageError ? null : modelNumberImageLink
	const imageLink = isImageDeleted ? croppedImageUrl : croppedImageUrl || imageLinkFromApi

	const onDrop = useCallback((acceptedFiles: File[]) => {
		if (acceptedFiles.length > 0) {
			setUploadedImage(acceptedFiles[0])
			setIsModalOpen(true)
		}
	}, [])

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop,
		accept: { "image/*": [] },
		maxSize: MAX_IMAGE_SIZE_BYTES,
		onDropRejected: () => error(`Image size must not exceed ${MAX_IMAGE_SIZE_MB}MB`),
	})

	const handleReplaceImage = () => {
		replaceInputRef.current?.click()
	}

	const handleReplaceFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0]
		if (file) {
			if (file.size > MAX_IMAGE_SIZE_BYTES) {
				error(`Image size must not exceed ${MAX_IMAGE_SIZE_MB}MB`)
				e.target.value = ""
				return
			}
			setUploadedImage(file)
			setIsModalOpen(true)
			e.target.value = ""
		}
	}

	const handleImageSave = (croppedUrl: string) => {
		if (!modelNumberId) return

		const imageFile = convertDataUrlToFile(croppedUrl, "model-number-image.png")
		uploadModelNumberImage(
			{ id: modelNumberId, image: imageFile },
			{
				onSuccess: () => {
					setIsImageDeleted(false)
					setCroppedImageUrl(croppedUrl)
					queryClient.invalidateQueries({ queryKey: ["getModelNumber", modelNumberId] })
					queryClient.invalidateQueries({ queryKey: ["getModelNumbers"] })
					success("Model number saved")
					setIsModalOpen(false)
				},
				onError: (e) => {
					formatError(e)
				},
			},
		)
	}

	const handleConfirmDelete = () => {
		setIsImageDeleted(true)
		setCroppedImageUrl(null)
		setUploadedImage(null)
		setIsDeleteModalOpen(false)
	}

	return (
		<>
			{imageLink ? (
				<div className={imageBlock}>
					<input
						ref={replaceInputRef}
						type="file"
						accept="image/*"
						style={{ display: "none" }}
						onChange={handleReplaceFileChange}
					/>
					<button className={replaceButton} onClick={handleReplaceImage}>
						Replace image
					</button>
					<button className={deleteButton} onClick={() => setIsDeleteModalOpen(true)}>
						Delete
					</button>
					<img src={imageLink} className={image} alt="Model image" />
				</div>
			) : (
				<ImageDropzone getRootProps={getRootProps} getInputProps={getInputProps} isDragActive={isDragActive} />
			)}
			<EditImageModal
				isModalOpen={isModalOpen}
				setModalClose={() => setIsModalOpen(false)}
				image={uploadedImage}
				onSave={handleImageSave}
				isSaving={isUploadPending}
			/>
			<Modal
				isModalOpen={isDeleteModalOpen}
				setModalClose={() => setIsDeleteModalOpen(false)}
				title="Delete image"
				width={456}
			>
				<>
					<p>Are you sure you want to delete model image?</p>
					<div className={deleteModalButtons}>
						<ButtonSecondary onClick={() => setIsDeleteModalOpen(false)}>Cancel</ButtonSecondary>
						<ButtonWarning onClick={handleConfirmDelete}>Yes, delete image</ButtonWarning>
					</div>
				</>
			</Modal>
		</>
	)
}
